/**
 * Approval queue presentational UI (WP28).
 * Approve ≠ send — Phase 1 never sends email.
 */

import type { DraftStatus, EmailDraftListItem } from "@carago/shared";
import { Link } from "react-router-dom";
import { formatDateTime, hotelDetailPath } from "../lib/hotels.js";

export type ApprovalsQueueViewState =
  | "loading"
  | "error"
  | "empty"
  | "ready";

export type ApprovalsQueueViewProps = {
  state: ApprovalsQueueViewState;
  errorMessage?: string | null;
  drafts: EmailDraftListItem[];
  statusFilter: DraftStatus | "";
  canApprove: boolean;
  selectedId: string | null;
  editSubject: string;
  editBody: string;
  rejectReason: string;
  actionMessage?: string | null;
  actionError?: string | null;
  busy?: boolean;
  hasNextPage: boolean;
  loadingMore: boolean;
  onStatusFilterChange: (value: DraftStatus | "") => void;
  onSelect: (id: string) => void;
  onEditSubjectChange: (value: string) => void;
  onEditBodyChange: (value: string) => void;
  onRejectReasonChange: (value: string) => void;
  onSaveEdit: () => void;
  onApprove: () => void;
  onReject: () => void;
  onLoadMore: () => void;
};

const STATUS_OPTIONS: { value: DraftStatus | ""; label: string }[] = [
  { value: "pending_approval", label: "Pending approval" },
  { value: "draft", label: "Draft" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "", label: "Alle" },
];

const STATUS_LABELS: Record<DraftStatus, string> = {
  draft: "Draft",
  pending_approval: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

/** Presentational approval queue (testable via react-dom/server). */
export function ApprovalsQueueView({
  state,
  errorMessage,
  drafts,
  statusFilter,
  canApprove,
  selectedId,
  editSubject,
  editBody,
  rejectReason,
  actionMessage,
  actionError,
  busy,
  hasNextPage,
  loadingMore,
  onStatusFilterChange,
  onSelect,
  onEditSubjectChange,
  onEditBodyChange,
  onRejectReasonChange,
  onSaveEdit,
  onApprove,
  onReject,
  onLoadMore,
}: ApprovalsQueueViewProps) {
  const selected = drafts.find((d) => d.id === selectedId) ?? null;
  const editable =
    selected != null &&
    selected.status !== "approved" &&
    selected.status !== "cancelled";

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4">
        <p className="text-sm text-gray-500">
          E-Mail-Entwürfe prüfen und freigeben. Freigabe bedeutet{" "}
          <span className="font-medium text-gray-700">nicht senden</span>{" "}
          (Phase 1).
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-3">
        <label className="min-w-[12rem]">
          <span className="mb-1 block text-xs font-semibold tracking-wide text-gray-400 uppercase">
            Status
          </span>
          <select
            data-testid="approvals-status-filter"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none focus:border-violet-500"
            value={statusFilter}
            onChange={(e) =>
              onStatusFilterChange(
                (e.target.value || "") as DraftStatus | "",
              )
            }
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {actionMessage ? (
        <p
          data-testid="approvals-action-message"
          className="mb-4 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
        >
          {actionMessage}
        </p>
      ) : null}
      {actionError ? (
        <p
          data-testid="approvals-action-error"
          className="mb-4 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          {actionError}
        </p>
      ) : null}

      {state === "loading" ? (
        <p data-testid="approvals-loading" className="text-sm text-gray-500">
          Lade Freigaben…
        </p>
      ) : null}

      {state === "error" ? (
        <div
          data-testid="approvals-error"
          className="rounded-lg border border-red-100 bg-red-100/40 px-4 py-3 text-sm text-red-800"
        >
          {errorMessage ?? "Entwürfe konnten nicht geladen werden."}
        </div>
      ) : null}

      {state === "empty" ? (
        <div
          data-testid="approvals-empty"
          className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-6 py-12 text-center"
        >
          <p className="text-sm font-medium text-gray-700">
            Keine Entwürfe in dieser Ansicht
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Sobald Research-Jobs Drafts erzeugen, erscheinen sie hier zur
            Freigabe.
          </p>
        </div>
      ) : null}

      {state === "ready" ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <ul
            data-testid="approvals-list"
            className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white"
          >
            {drafts.map((draft) => {
              const active = draft.id === selectedId;
              return (
                <li key={draft.id}>
                  <button
                    type="button"
                    data-testid={`approvals-item-${draft.id}`}
                    className={`w-full px-4 py-3 text-left transition ${
                      active
                        ? "bg-violet-50"
                        : "hover:bg-gray-50"
                    }`}
                    onClick={() => onSelect(draft.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-800">
                          {draft.hotel_name}
                        </p>
                        <p className="mt-0.5 truncate text-sm text-gray-600">
                          {draft.subject}
                        </p>
                      </div>
                      <span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        {STATUS_LABELS[draft.status]}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-400">
                      {formatDateTime(draft.updated_at)} · {draft.purpose}
                    </p>
                  </button>
                </li>
              );
            })}
          </ul>

          <div
            data-testid="approvals-detail"
            className="rounded-lg border border-gray-200 bg-white p-4"
          >
            {!selected ? (
              <p className="text-sm text-gray-500">
                Wähle einen Entwurf aus der Liste.
              </p>
            ) : (
              <div className="space-y-4">
                <div>
                  <Link
                    to={hotelDetailPath(selected.hotel_id)}
                    className="text-sm font-semibold text-violet-700 hover:underline"
                  >
                    {selected.hotel_name}
                  </Link>
                  <p className="mt-1 text-xs text-gray-400">
                    Status: {STATUS_LABELS[selected.status]}
                    {selected.rejection_reason
                      ? ` · Ablehnung: ${selected.rejection_reason}`
                      : ""}
                  </p>
                </div>

                <label className="block">
                  <span className="mb-1 block text-xs font-semibold tracking-wide text-gray-400 uppercase">
                    Betreff
                  </span>
                  <input
                    data-testid="approvals-edit-subject"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none focus:border-violet-500 disabled:bg-gray-50"
                    value={editSubject}
                    disabled={!editable || busy}
                    onChange={(e) => onEditSubjectChange(e.target.value)}
                  />
                </label>

                <label className="block">
                  <span className="mb-1 block text-xs font-semibold tracking-wide text-gray-400 uppercase">
                    Text
                  </span>
                  <textarea
                    data-testid="approvals-edit-body"
                    rows={10}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm text-gray-800 outline-none focus:border-violet-500 disabled:bg-gray-50"
                    value={editBody}
                    disabled={!editable || busy}
                    onChange={(e) => onEditBodyChange(e.target.value)}
                  />
                </label>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid="approvals-save"
                    disabled={!editable || busy}
                    onClick={onSaveEdit}
                    className="rounded-lg bg-gray-800 px-3 py-2 text-sm font-medium text-white disabled:opacity-40"
                  >
                    Speichern
                  </button>
                  {canApprove ? (
                    <>
                      <button
                        type="button"
                        data-testid="approvals-approve"
                        disabled={
                          busy ||
                          (selected.status !== "pending_approval" &&
                            selected.status !== "draft")
                        }
                        onClick={onApprove}
                        className="rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-40"
                      >
                        Freigeben
                      </button>
                      <button
                        type="button"
                        data-testid="approvals-reject"
                        disabled={busy || selected.status === "rejected"}
                        onClick={onReject}
                        className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800 disabled:opacity-40"
                      >
                        Ablehnen
                      </button>
                    </>
                  ) : (
                    <p
                      data-testid="approvals-role-hint"
                      className="self-center text-xs text-gray-500"
                    >
                      Freigeben/Ablehnen erfordert Approver- oder Admin-Rolle.
                    </p>
                  )}
                </div>

                {canApprove ? (
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold tracking-wide text-gray-400 uppercase">
                      Ablehnungsgrund (optional)
                    </span>
                    <input
                      data-testid="approvals-reject-reason"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none focus:border-violet-500"
                      value={rejectReason}
                      disabled={busy}
                      onChange={(e) => onRejectReasonChange(e.target.value)}
                      placeholder="z. B. Ton anpassen"
                    />
                  </label>
                ) : null}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {state === "ready" && hasNextPage ? (
        <div className="mt-4">
          <button
            type="button"
            data-testid="approvals-load-more"
            disabled={loadingMore}
            onClick={onLoadMore}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 disabled:opacity-40"
          >
            {loadingMore ? "Lade…" : "Mehr laden"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
